import { useEffect, useSyncExternalStore } from 'react';

/**
 * Body scroll lock: single source of truth.
 *
 * Every modal / drawer that must freeze background scroll goes through here
 * (never `document.body.style.overflow = ...` directly). A module-level
 * counter makes overlapping overlays safe: the body locks on the first
 * holder and unlocks only when the last one releases, whatever the close order.
 */
let lockCount = 0;
let previousOverflow = '';
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((l) => l());
};

/** Acquire a lock. Returns an idempotent release function. */
export function lockBodyScroll(): () => void {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.style.overflow = previousOverflow;
    }
    notify();
  };
}

/** Lock body scroll while `active` is true. */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    return lockBodyScroll();
  }, [active]);
}

const subscribe = (onChange: () => void): (() => void) => {
  listeners.add(onChange);
  // Radix dialogs lock via react-remove-scroll (data-scroll-locked), outside our counter.
  const observer = new MutationObserver(onChange);
  observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });
  return () => {
    listeners.delete(onChange);
    observer.disconnect();
  };
};

const getSnapshot = (): boolean =>
  lockCount > 0 || document.body.hasAttribute('data-scroll-locked');

/** True while any scroll lock (ours or Radix's) is held, i.e. an overlay is open. */
export function useIsBodyScrollLocked(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
